import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Download, Users, CheckCircle2, TrendingUp, Edit, Share2, LogIn } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionnaireResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  
  const copyShareLink = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  };

  useEffect(() => {
    fetchSessionAndStats();
    const interval = setInterval(fetchSessionAndStats, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [sessionId]);

  const fetchSessionAndStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsLoggedIn(false);
      }
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch session
      const sessionRes = await axios.get(`${API}/sessions/${sessionId}`);
      setSession(sessionRes.data);
      
      // Fetch statistics
      const statsRes = await axios.get(`${API}/sessions/${sessionId}/questionnaire-statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(statsRes.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load questionnaire results');
      setLoading(false);
    }
  };

  const downloadResponses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}/questionnaire-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to CSV
      const responses = response.data;
      if (responses.length === 0) {
        toast.error('No responses to download');
        return;
      }
      
      // Helper function to convert response to human-readable format
      const formatResponse = (resp) => {
        const question = session.questions.find(q => q.id === resp.question_id);
        if (!question) return JSON.stringify(resp.response);
        
        const responseType = question.response_type;
        const responseData = resp.response;
        
        if (responseType === 'star_rating') {
          return `${responseData.rating} stars`;
        } else if (responseType === 'mcq_response' || responseType === 'image_mcq_response') {
          const selectedOption = question.options?.find(opt => opt.id === responseData.answer);
          return selectedOption ? selectedOption.text : responseData.answer;
        } else if (responseType === 'drag_reorder_ranking') {
          const ranking = responseData.ranking || [];
          const rankedTexts = ranking.map((optId, index) => {
            const option = question.options?.find(opt => opt.id === optId);
            return `${index + 1}. ${option ? option.text : optId}`;
          });
          return rankedTexts.join(' | ');
        }
        
        return JSON.stringify(responseData);
      };
      
      // Create CSV content with human-readable responses
      const headerLabel = session.is_anonymous ? "Participant ID" : "Participant Name";
      let csvContent = `${headerLabel},Question Text,Response,Score,Timestamp\n`;
      responses.forEach(resp => {
        const question = session.questions.find(q => q.id === resp.question_id);
        const questionText = question ? question.text : resp.question_id;
        const readableResponse = formatResponse(resp);
        
        // For non-anonymous, try to extract name from participant_id
        let participantDisplay = resp.participant_id;
        if (!session.is_anonymous && resp.participant_id.includes('_')) {
          // Extract name part (before timestamp)
          const parts = resp.participant_id.split('_');
          if (parts.length > 0) {
            participantDisplay = parts.slice(0, -1).join(' ').replace(/_/g, ' ');
            // Capitalize first letter of each word
            participantDisplay = participantDisplay.split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
          }
        }
        
        // Escape quotes in CSV
        const escapedParticipant = participantDisplay.replace(/"/g, '""');
        const escapedQuestion = questionText.replace(/"/g, '""');
        const escapedResponse = readableResponse.replace(/"/g, '""');
        
        csvContent += `"${escapedParticipant}","${escapedQuestion}","${escapedResponse}",${resp.score || ''},${resp.timestamp}\n`;
      });
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questionnaire_${sessionId}_responses.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Responses downloaded');
    } catch (error) {
      toast.error('Failed to download responses');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!session || !statistics) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <div className="text-center">
          <p className="text-gray-600">Session not found</p>
          <Button onClick={() => {
            const referrer = sessionStorage.getItem('referrer') || '/dashboard';
            navigate(isLoggedIn ? referrer : '/login');
          }} className="mt-4">
            {isLoggedIn ? 'Back' : 'Login / Sign Up'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-4">
          <PollarBrandMini />
        </div>

        <div className="flex items-center gap-3 mb-6">
          <Button
            onClick={() => {
              const referrer = sessionStorage.getItem('referrer') || '/dashboard';
              navigate(isLoggedIn ? referrer : '/login');
            }}
            variant="ghost"
          >
            {isLoggedIn ? (
              <>
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-5 w-5" />
                Login / Sign Up
              </>
            )}
          </Button>
          
          {/* Share Link Button */}
          <Button
            onClick={copyShareLink}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Share2 className="h-4 w-4" />
            Share Link
          </Button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#1a1a1a' }}>
                {session.topic}
              </h1>
              <p className="text-gray-600 mb-4">Questionnaire Results - Live Updates</p>
              
              {/* Shareable Link */}
              <div className="mt-4">
                <Label className="text-sm font-semibold mb-2 block">Share with Participants:</Label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/participant/${session.id}`}
                    readOnly
                    className="flex-1 px-3 py-2 border rounded text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/participant/${session.id}`);
                      toast.success('Link copied!');
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </div>
            
            {/* QR Code */}
            <div className="flex justify-center items-center">
              {session.qr_code_data && (
                <div className="text-center">
                  <img 
                    src={session.qr_code_data} 
                    alt="QR Code" 
                    className="w-40 h-40 mx-auto"
                  />
                  <p className="text-xs text-gray-500 mt-2">Scan to participate</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {statistics.average_score !== null && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Average Score</p>
                    <p className="text-2xl font-bold">{statistics.average_score.toFixed(1)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Started</p>
                  <p className="text-2xl font-bold">{statistics.respondents_started}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold">{statistics.respondents_completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex gap-4 flex-wrap">
          <Button
            onClick={downloadResponses}
            variant="outline"
          >
            <Download className="w-4 h-4 mr-2" />
            Download All Responses (CSV)
          </Button>
          <Button
            onClick={() => navigate(`/facilitator/${sessionId}/edit`)}
            variant="outline"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Settings
          </Button>
        </div>

        {/* Question Response Section */}
        <div className="space-y-6">
          {statistics.question_statistics.map((qStat, index) => (
            <Card key={qStat.question_id}>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Question {index + 1}: {qStat.question_text}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Total Responses: {qStat.total_responses}
                </p>

                {/* MCQ / Image MCQ */}
                {['mcq_response', 'image_mcq_response'].includes(qStat.response_type) && (
                  <div className="space-y-2">
                    {qStat.responses.map((option, idx) => {
                      const percentage = qStat.total_responses > 0 
                        ? (option.count / qStat.total_responses * 100).toFixed(1)
                        : 0;
                      
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span className={option.is_correct ? 'font-bold text-green-600' : ''}>
                              {option.text} {option.is_correct && '✓ (Correct)'}
                            </span>
                            <span>{option.count} ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                option.is_correct ? 'bg-green-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          {qStat.response_type === 'image_mcq_response' && option.image_url && (
                            <img src={option.image_url} alt={option.text} className="w-16 h-16 object-cover rounded mt-1" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Ranking */}
                {qStat.response_type === 'drag_reorder_ranking' && (
                  <div className="space-y-2">
                    {qStat.responses.map((option, idx) => {
                      const maxPoints = qStat.responses[0]?.points || 1;
                      const percentage = (option.points / maxPoints * 100).toFixed(1);
                      
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium">#{idx + 1} {option.text}</span>
                            <span>{option.points} pts</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div
                              className="h-full bg-purple-500 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Star Rating */}
                {qStat.response_type === 'star_rating' && (
                  <div className="space-y-2">
                    {qStat.responses.map((rating) => {
                      const percentage = qStat.total_responses > 0 
                        ? (rating.count / qStat.total_responses * 100).toFixed(1)
                        : 0;
                      
                      return (
                        <div key={rating.rating} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span>{'⭐'.repeat(rating.rating)} ({rating.rating} stars)</span>
                            <span>{rating.count} ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div
                              className="h-full bg-yellow-500 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireResults;
